import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { apiService } from './services/api';
import Layout from './components/Layout';
import Login from './pages/Login';
import LoginCallback from './pages/LoginCallback';
import SiteTokens from './pages/SiteTokens';
import SiteUsers from './pages/SiteUsers';
import SiteMachines from './pages/SiteMachines';
import AdminUsers from './pages/AdminUsers';
import UserRegionAccess from './pages/UserRegionAccess';
import FeedbackPage from './pages/Feedback';
import './App.css';

interface User {
  id: number;
  username: string;
  email?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
}

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  auth: AuthState;
}> = ({ children, auth }) => {
  if (auth.isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{
  children: React.ReactNode;
  auth: AuthState;
}> = ({ children, auth }) => {
  if (auth.isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!auth.user?.is_superuser) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
  });

  useEffect(() => {
    if (!window.location.pathname.includes('/login/callback')) {
      checkAuthStatus();
    } else {
      setAuth(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await apiService.checkAuth();
      setAuth({
        isAuthenticated: response.authenticated,
        user: response.user || null,
        isLoading: false,
      });
    } catch {
      setAuth({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
    }
  };

  if (auth.isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <Toaster position="top-right" toastOptions={{ duration: 5000 }} />
      <div className="App">
        <Routes>
          <Route
            path="/login"
            element={
              auth.isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Login />
              )
            }
          />
          <Route path="/login/callback" element={<LoginCallback />} />
          <Route path="/" element={
            <ProtectedRoute auth={auth}>
              <Layout username={auth.user?.username} isSuperuser={auth.user?.is_superuser}>
                <SiteTokens />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/site-users" element={
            <ProtectedRoute auth={auth}>
              <Layout username={auth.user?.username} isSuperuser={auth.user?.is_superuser}>
                <SiteUsers />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/site-machines" element={
            <ProtectedRoute auth={auth}>
              <Layout username={auth.user?.username} isSuperuser={auth.user?.is_superuser}>
                <SiteMachines />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin-users" element={
            <AdminRoute auth={auth}>
              <Layout username={auth.user?.username} isSuperuser={auth.user?.is_superuser}>
                <AdminUsers />
              </Layout>
            </AdminRoute>
          } />
          <Route path="/user-region-access" element={
            <ProtectedRoute auth={auth}>
              <Layout username={auth.user?.username} isSuperuser={auth.user?.is_superuser}>
                <UserRegionAccess />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/feedback" element={
            <ProtectedRoute auth={auth}>
              <Layout username={auth.user?.username} isSuperuser={auth.user?.is_superuser}>
                <FeedbackPage />
              </Layout>
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
